import {
    CollisionDetection,
    pointerWithin,
    rectIntersection,
    closestCenter,
    getFirstCollision,
    UniqueIdentifier
} from '@dnd-kit/core'
import debugModule from 'debug'

import { DragData, ParsedRow, isSpecialRow } from './types'
import { MAX_ELEMENTS_PER_ROW, PLACEHOLDER_ELEMENT_ID } from './constants'

const debug = debugModule('app:SchemaBuilder:collision')

interface CollisionDetectionArgs {
    parsedRows: ParsedRow[]
    findRowForElement: (elementId: UniqueIdentifier) => ParsedRow | null
    isRowFull: (rowIndex: number, excludeElementId?: UniqueIdentifier) => boolean
}

/**
 * Custom collision detection strategy optimized for the form builder.
 *
 * Based on the official dnd-kit MultipleContainers example.
 * Key insight: filter droppable containers based on what's being dragged:
 * - Layout items can only target other layout items (rows) or the board
 * - Elements can only target elements within non-full, non-special rows or empty rows
 */
export const createCollisionDetection = ({
    parsedRows,
    findRowForElement,
    isRowFull
}: CollisionDetectionArgs): CollisionDetection => {
    // Track the last valid overId to prevent null flickering during layout shifts
    let lastOverId: UniqueIdentifier | null = null

    const collisionDetection: CollisionDetection = (args) => {
        const { active, droppableContainers } = args
        const activeData = active.data.current as DragData | undefined

        debug('Collision detection', {
            activeId: active.id,
            activeType: activeData?.type,
            fromToolbox: activeData?.fromToolbox,
            containerCount: droppableContainers.length
        })

        // Determine which containers are valid drop targets based on what's being dragged
        let validContainers = [...droppableContainers]

        if (activeData?.type === 'layout') {
            // When dragging a layout item:
            // - Only allow dropping on other layout items (rows) and form-builder-droppable
            // - Filter out all elements and toolbox items
            validContainers = droppableContainers.filter(container => {
                const containerData = container.data.current as DragData | undefined
                // Allow form-builder-droppable
                if (container.id === 'form-builder-droppable') return true
                // Allow other layout items (rows) that are not from toolbox
                if (containerData?.type === 'layout' && !containerData.fromToolbox) return true
                return false
            })

            debug('Layout drag: filtered to', validContainers.length, 'containers')
        } else if (activeData?.type === 'element') {
            // When dragging an element:
            // - Only allow dropping on elements within non-special rows OR empty non-special rows
            // - Filter out special rows (spacer/list), full rows (when coming from different row), and toolbox items

            // Find which row the active element is currently in (for existing elements)
            const activeElementRow = activeData.fromToolbox
                ? null
                : findRowForElement(active.id)

            validContainers = droppableContainers.filter(container => {
                const containerData = container.data.current as DragData | undefined

                // Filter out toolbox items
                if (containerData?.fromToolbox) return false

                // Filter out layout items - elements should target elements only
                if (containerData?.type === 'layout') {
                    return false
                }

                // Allow elements (including empty row droppables), but filter out elements in full/special rows
                if (containerData?.type === 'element' && containerData.rowIndex !== undefined) {
                    // Check if target row is a special row (spacer/list)
                    const targetRow = parsedRows[containerData.rowIndex]
                    if (targetRow && isSpecialRow(targetRow)) {
                        return false
                    }

                    const isMovingWithinSameRow = activeElementRow?.index === containerData.rowIndex
                    if (!isMovingWithinSameRow && isRowFull(containerData.rowIndex, active.id)) {
                        return false
                    }
                    return true
                }

                return false
            })

            debug('Element drag: filtered to', validContainers.length, 'containers')
        }

        // Now run collision detection with only valid containers
        const filteredArgs = { ...args, droppableContainers: validContainers }

        // Start by finding any intersecting droppable
        const pointerCollisions = pointerWithin(filteredArgs)
        const intersections = pointerCollisions.length > 0
            ? pointerCollisions
            : rectIntersection(filteredArgs)

        let overId = getFirstCollision(intersections, 'id')

        if (overId != null) {
            // If we're over a row that has items, find the closest item within that row
            const overRowIndex = typeof overId === 'string' && overId.startsWith('row-')
                ? parseInt(overId.substring(4), 10)
                : null

            if (overRowIndex !== null) {
                const overRow = parsedRows[overRowIndex]
                if (overRow && !isSpecialRow(overRow) && overRow.items.length > 0) {
                    // Return the closest droppable within that row
                    const closestItem = closestCenter({
                        ...filteredArgs,
                        droppableContainers: validContainers.filter(
                            (container) => {
                                const containerData = container.data.current as DragData | undefined
                                return containerData?.type === 'element' &&
                                       containerData.rowIndex === overRowIndex
                            }
                        )
                    })[0]?.id

                    if (closestItem) {
                        overId = closestItem
                    }
                }
            }

            lastOverId = overId
            debug('Collision result:', overId)
            return [{ id: overId }]
        }

        // When a draggable item moves to a new container, the layout may shift
        // and the overId may become null. We use the cached lastOverId to prevent flickering.
        // BUT: only do this for existing elements being moved, NOT for toolbox items.
        // Toolbox items should return null so the placeholder can be removed.
        if (activeData?.fromToolbox) {
            // For toolbox items, return empty array (null) so handleDragOver fires with over=null
            lastOverId = null
            debug('Toolbox item: clearing lastOverId')
            return []
        }

        // If no droppable is matched, return the last match to prevent null flickering
        if (lastOverId) {
            debug('Using cached lastOverId:', lastOverId)
        }
        return lastOverId ? [{ id: lastOverId }] : []
    }

    // Add a reset function to clear the cached lastOverId
    ;(collisionDetection as any).reset = () => {
        lastOverId = null
    }

    return collisionDetection
}

/**
 * Reset the collision detection cache.
 * Call this when drag ends or cancels.
 */
export const resetCollisionCache = (collisionDetection: CollisionDetection) => {
    if ((collisionDetection as any).reset) {
        (collisionDetection as any).reset()
    }
}
